/**
 * src/state/vendorPatch.js
 *
 * GH #7979 (TRYGGR_INTENT.md §13 c/d) — the pure half of field-level vendor
 * writes. vendors-write `bulk-update` takes { vendorIds, patch, expected } and
 * merges only the patched fields, so two users editing DIFFERENT fields of one
 * vendor both land; a field someone else changed since it was loaded comes
 * back as a 409 naming it (`error.conflicts`).
 *
 * The comparison and the server-owned field list are the server's own
 * (azure-functions/shared/vendor/patchKeys.cjs), so the client never sends a
 * field the server would refuse, nor thinks a field changed when the server
 * would not.
 */
import {
  VENDOR_SERVER_OWNED_KEYS,
  sameValue,
} from '../../azure-functions/shared/vendor/patchKeys.cjs';

const SERVER_OWNED = new Set(VENDOR_SERVER_OWNED_KEYS);

/**
 * The fields the user changed in an edit, and the value each held when the
 * edit was opened. `base` is the vendor as it was when the form opened.
 * @returns {{ patch: object, expected: object }} expected is keyed by field
 */
export function diffVendor(base, edited) {
  const patch = {};
  const expected = {};
  const b = base || {};
  for (const field of Object.keys(edited || {})) {
    if (SERVER_OWNED.has(field)) continue;
    if (sameValue(b[field], edited[field])) continue;
    patch[field] = edited[field];
    expected[field] = b[field] ?? null;
  }
  return { patch, expected };
}

/**
 * The `expected` map for one patch applied to many vendors: for each id, the
 * value each patched field holds on the row the user is looking at.
 */
export function expectedFromRows(ids, patch, vendors) {
  const byId = new Map((vendors || []).map((v) => [v?.id, v]));
  const out = {};
  for (const id of ids) {
    const row = byId.get(id) || {};
    out[id] = Object.fromEntries(Object.keys(patch).map((f) => [f, row[f] ?? null]));
  }
  return out;
}

/**
 * The user's answer to a field clash, applied to the edit before it is sent
 * again.
 *   'mine'   — the stored value becomes the loaded one, so the user's value
 *              is written over it (expected = theirs);
 *   'theirs' — the clashing fields take the stored value and drop out of the
 *              patch; the rest of the user's edit is still sent.
 * Only clashes for `vendorId` (when given) are applied.
 * @returns {{ base: object, edited: object }}
 */
export function resolveVendorClash(base, edited, conflicts, mode, vendorId) {
  const nextBase = { ...(base || {}) };
  const nextEdited = { ...(edited || {}) };
  for (const c of conflicts || []) {
    if (!c || !c.field) continue;
    if (vendorId && c.vendorId && c.vendorId !== vendorId) continue;
    nextBase[c.field] = c.theirs;
    if (mode === 'theirs') nextEdited[c.field] = c.theirs;
  }
  return { base: nextBase, edited: nextEdited };
}

/**
 * Overlay what the server holds now at each clashing field onto a vendor
 * list, so the screen shows the stored value rather than the refused one.
 */
export function overlayTheirs(vendors, conflicts) {
  const byVendor = new Map();
  for (const c of conflicts || []) {
    if (!c || !c.vendorId || !c.field) continue;
    if (!byVendor.has(c.vendorId)) byVendor.set(c.vendorId, {});
    byVendor.get(c.vendorId)[c.field] = c.theirs;
  }
  if (!byVendor.size) return vendors;
  return (vendors || []).map((v) =>
    v && byVendor.has(v.id) ? { ...v, ...byVendor.get(v.id) } : v
  );
}

/** A vendor clash may always be overwritten: vendor status is a plain field
 *  (archiving is its own action, and the server refuses it in a patch). */
export function isVendorClashKeepable(c) {
  return !!c && c.kind === 'changed' && typeof c.field === 'string';
}

// ── Bulk edit clash answers (GH #7979) ─────────────────────────────────────
// A pending bulk edit is { patches: {[vendorId]: {field: value}}, expected:
// {[vendorId]: {field: loadedValue}} } exactly as last sent. Each answer is
// derived from it and the conflicts the server returned — never from the
// register as it is now, which may have moved since the clash was raised.

function copyPending(pending) {
  const patches = Object.create(null);
  const expected = Object.create(null);
  for (const id of Object.keys(pending?.patches || {})) {
    patches[id] = { ...pending.patches[id] };
    expected[id] = { ...(pending.expected?.[id] || {}) };
  }
  return { patches, expected };
}

function plain({ patches, expected }) {
  const out = { patches: {}, expected: {} };
  for (const id of Object.keys(patches)) {
    if (!Object.keys(patches[id]).length) continue;
    Object.defineProperty(out.patches, id, {
      value: patches[id],
      enumerable: true,
      writable: true,
    });
    Object.defineProperty(out.expected, id, {
      value: expected[id],
      enumerable: true,
      writable: true,
    });
  }
  return out;
}

/** Keep mine: the same edit, expecting what is stored now at each clash. */
export function keepMineVendorPatches(pending, conflicts) {
  const next = copyPending(pending);
  for (const c of conflicts || []) {
    if (!c?.vendorId || !c.field || !next.patches[c.vendorId]) continue;
    if (!Object.prototype.hasOwnProperty.call(next.patches[c.vendorId], c.field)) continue;
    next.expected[c.vendorId][c.field] = c.theirs ?? null;
  }
  return plain(next);
}

/** Use theirs: each clashing (vendor, field) leaves the edit; the rest stays. */
export function takeTheirsVendorPatches(pending, conflicts) {
  const next = copyPending(pending);
  for (const c of conflicts || []) {
    if (!c?.vendorId || !c.field || !next.patches[c.vendorId]) continue;
    delete next.patches[c.vendorId][c.field];
    delete next.expected[c.vendorId][c.field];
  }
  return plain(next);
}

/** The edit without the named vendors (deleted meanwhile). */
export function withoutVendors(pending, vendorIds) {
  const next = copyPending(pending);
  for (const id of vendorIds || []) {
    delete next.patches[id];
    delete next.expected[id];
  }
  return plain(next);
}

/** One row per vendor still in the edit: its name and the fields it sets. */
export function describeVendorPatches(pending) {
  return Object.keys(pending?.patches || {}).map((vendorId) => ({
    vendorId,
    name: pending.names?.[vendorId] || vendorId,
    fields: Object.keys(pending.patches[vendorId]),
  }));
}
